/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { Team } from '../types';
import { PersonOutline } from '@mui/icons-material';
import CreatorInviteLinks from './CreatorInviteLinks';

interface TeamInfoProps {
  team: Team;
  isUserTeam: boolean;
  isCurrentTurn: boolean;
  usernames?: {[key: string]: string};
  yourTeamText?: string;
  teamMembersText?: string;
  currentTurnText?: string;
  waitingText?: string;
  isCreator?: boolean;
  debateId?: string;
  opponentTeam?: Team;
  opponentInviteLink?: string;
}

const TeamInfo: React.FC<TeamInfoProps> = ({ 
  team, 
  isUserTeam, 
  isCurrentTurn, 
  usernames = {}, 
  yourTeamText, 
  teamMembersText, 
  currentTurnText, 
  waitingText,
  isCreator = false,
  debateId,
  opponentTeam,
  opponentInviteLink
}) => {
  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 2, 
        mb: 2,
        bgcolor: isUserTeam ? '#f8f9ff' : '#fff',
        border: isCurrentTurn ? '2px solid #4caf50' : 'none',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {team.name}
          {isUserTeam && (
            <Chip 
              size="small" 
              label={yourTeamText || "Your Team"} 
              color="primary" 
              sx={{ ml: 1, fontSize: '0.7rem', fontWeight: 'bold' }} 
            />
          )}
        </Typography>
        {isCurrentTurn && (
          <Chip 
            size="small" 
            label={currentTurnText || "Current Turn"} 
            color="secondary" 
            sx={{ fontSize: '0.7rem', fontWeight: 'bold' }} 
          />
        )}
      </Box>
      
      <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
        {team.viewpoint}
      </Typography>
      
      {/* Display team members */}
      {team.members && team.members.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {teamMembersText || "Team Members:"}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {team.members.map((memberId, index) => (
              <Chip
                key={index}
                icon={<PersonOutline />}
                label={usernames[memberId] || `Member ${index + 1}`}
                size="small"
                variant="outlined"
                sx={{ 
                  bgcolor: '#f1f5f9',
                  '& .MuiChip-icon': { fontSize: 16 }
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Show invite links for creators */}
      {isCreator && isUserTeam && debateId && opponentTeam && (
        <CreatorInviteLinks
          debateId={debateId}
          teamId={team.id}
          teamName={team.name}
          teamSize={team.maxMembers || 1}
          opponentTeamName={opponentTeam.name}
          opponentTeamSize={opponentTeam.maxMembers || 1}
          isCreator={true}
          opponentInviteLink={opponentInviteLink}
          teamInviteCode={team.inviteCode}
        />
      )}
    </Paper>
  );
};

export default TeamInfo; 